<script setup lang="ts">
import { UiButton, UiSelect, UiTextInput } from "@/components"
import type { FieldErrorResolver, InstallmentErrorResolver, SalesOrderFormState } from "./types"

const props = defineProps<{
  formState: SalesOrderFormState
  fieldError: FieldErrorResolver
  installmentError: InstallmentErrorResolver
  paymentOptions: Array<{ label: string; value: string }>
  isInstallmentPayment: boolean
}>()

const emit = defineEmits<{
  "add-installment": []
  "remove-installment": [index: number]
}>()
</script>

<template>
  <section class="rounded-md border border-slate-200 p-4">
    <h2 class="mb-3 text-sm font-semibold text-slate-800">Condicoes comerciais</h2>

    <div class="grid gap-4 md:grid-cols-4">
      <UiSelect
        id="sales-order-payment-terms"
        :model-value="formState.payment_terms"
        label="Pagamento"
        :options="paymentOptions"
        :error="fieldError('payment_terms')"
        @update:model-value="formState.payment_terms = String($event || '')"
      />

      <UiTextInput
        id="sales-order-validity-date"
        v-model="formState.validity_date"
        type="date"
        label="Validade"
        :error="fieldError('validity_date')"
      />

      <UiSelect
        id="sales-order-freight-mode"
        :model-value="formState.freight_mode"
        label="Frete"
        :options="[
          { label: 'CIF', value: 'cif' },
          { label: 'FOB', value: 'fob' },
          { label: 'Retira', value: 'retira' },
        ]"
        :error="fieldError('freight_mode')"
        @update:model-value="formState.freight_mode = String($event || '')"
      />

      <UiTextInput
        id="sales-order-commercial-notes"
        v-model="formState.commercial_notes"
        label="Observacoes"
        :error="fieldError('commercial_notes')"
      />
    </div>

    <div v-if="isInstallmentPayment" class="mt-4 rounded-md border border-slate-200 p-3">
      <div class="mb-3 flex items-center justify-between">
        <h3 class="text-sm font-semibold text-slate-700">Parcelas</h3>
        <UiButton type="button" size="sm" variant="secondary" @click="emit('add-installment')">Adicionar parcela</UiButton>
      </div>

      <div class="space-y-3">
        <div v-for="(installment, index) in props.formState.installments" :key="index" class="grid gap-3 md:grid-cols-3">
          <UiTextInput
            :id="`sales-order-installment-date-${index}`"
            v-model="installment.due_date"
            type="date"
            label="Vencimento"
            :error="installmentError(index, 'due_date')"
          />

          <UiTextInput
            :id="`sales-order-installment-amount-${index}`"
            v-model="installment.amount"
            type="number"
            step="0.01"
            label="Valor"
            :error="installmentError(index, 'amount')"
          />

          <div class="flex items-end">
            <UiButton type="button" size="sm" variant="danger" @click="emit('remove-installment', index)">Remover</UiButton>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

